import { uuid25 } from "../../../uuid/dist/uuid25.js";
import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { Tag, attachActionTag } from "../../tag.js";
import { SelectType } from "../editor/state.js";
import { unionRect } from "../../../konva/scene/metrics.js";
import { findTableGroupsAt, getTableCenter, getTableGroupId, getTableGroupMemberIds, getTableGroupRects, getTablesGroupRect, isPointInRect, isTableGroupShown, nextTableGroupZIndex } from "../../../utils/tableGroup.js";
import { selectAction } from "../editor/atom.actions.js";
import { changeTableGroupAction, changeZIndexAction, moveTableAction } from "../table/atom.actions.js";
import { addTableGroupAction, changeTableGroupNameAction, changeTableGroupZIndexAction, moveTableGroupAction, removeTableGroupAction } from "./atom.actions.js";
import { getSelectTypeIds } from "../editor/utils/selection.js";
import { unselectAllAction$ } from "../editor/generator.actions.js";
import { uniq } from "es-toolkit";
//#region src/engine/modules/table-group/generator.actions.ts
var selectGroups = ({ doc, collections }) => query(collections).collection("tableGroupEntities").selectByIds(doc.tableGroupIds);
function* addGroup$(state, rect, tableIds, { name, color }) {
	const id = uuid25();
	const { x, y, width, height } = rect;
	yield unselectAllAction$();
	yield addTableGroupAction({
		id,
		...color ? { color } : {},
		ui: {
			x,
			y,
			width,
			height,
			zIndex: nextTableGroupZIndex(selectGroups(state))
		}
	});
	yield selectAction({ [id]: SelectType.tableGroup });
	if (name) yield changeTableGroupNameAction({
		id,
		value: name
	});
	yield tableIds.map((tableId) => changeTableGroupAction({
		id: tableId,
		value: id
	}));
}
var addTableGroupAction$ = (rect, fields = {}) => function* (state) {
	const { doc, collections } = state;
	yield* addGroup$(state, rect, query(collections).collection("tableEntities").selectByIds(doc.tableIds).filter((table) => !getTableGroupId(state, table) && isPointInRect(getTableCenter(state, table), rect)).map(({ id }) => id), fields);
};
var addTableGroupFromTablesAction$ = (tableIds, fields = {}) => function* (state) {
	const ids = uniq(tableIds ?? getSelectTypeIds(state.editor.selectedMap).tableIds).filter(arrayHas(state.doc.tableIds));
	const rect = getTablesGroupRect(state, ids);
	if (!rect) return;
	yield* addGroup$(state, rect, ids, fields);
};
var removeTableGroupAction$ = (id) => function* (state) {
	const { doc: { tableGroupIds }, editor: { selectedMap } } = state;
	const isInDoc = arrayHas(tableGroupIds);
	const ids = id ? [id] : Object.entries(selectedMap).filter(([id, type]) => type === SelectType.tableGroup && isInDoc(id)).map(([id]) => id);
	for (const groupId of ids) {
		yield getTableGroupMemberIds(state, groupId).map((tableId) => changeTableGroupAction({
			id: tableId,
			value: ""
		}));
		yield removeTableGroupAction({ id: groupId });
	}
};
function getCarriedTableIds(state, groupIds, tableIds) {
	return uniq([...tableIds, ...groupIds.flatMap((id) => getTableGroupMemberIds(state, id))]);
}
function toMoveTableGroupActions(groupIds, tableIds, movementX, movementY) {
	return [...groupIds.length ? [moveTableGroupAction({
		ids: groupIds,
		movementX,
		movementY
	})] : [], ...tableIds.length ? [moveTableAction({
		ids: tableIds,
		movementX,
		movementY
	})] : []].map((action) => attachActionTag(Tag.drag, action));
}
var moveTableGroupAction$ = (ids, movementX, movementY) => function* (state) {
	yield toMoveTableGroupActions(ids, getCarriedTableIds(state, ids, []), movementX, movementY);
};
var setTableGroupAction$ = (tableIds, groupId) => function* ({ doc, collections }) {
	if (groupId && !doc.tableGroupIds.includes(groupId)) return;
	yield query(collections).collection("tableEntities").selectByIds(uniq(tableIds).filter(arrayHas(doc.tableIds))).filter((table) => table.groupId !== groupId).map(({ id }) => changeTableGroupAction({
		id,
		value: groupId
	}));
};
function getTableGroupDrops(state, held) {
	if (!isTableGroupShown(state)) return [];
	const { doc, collections, editor } = state;
	const { tableIds, tableGroupIds } = getSelectTypeIds(editor.selectedMap);
	const listed = new Set(doc.tableIds);
	const selected = tableIds.filter((id) => listed.has(id));
	if (!selected.length) return [];
	const carried = new Set(tableGroupIds.flatMap((id) => getTableGroupMemberIds(state, id)));
	const judged = selected.filter((id) => !carried.has(id));
	if (!judged.length) return [];
	const moving = new Set(tableGroupIds);
	const boxes = getTableGroupRects(state, { excludeTableIds: judged });
	held?.forEach((rect, id) => {
		const box = boxes.get(id);
		if (box && !moving.has(id)) boxes.set(id, unionRect(box, rect));
	});
	const tables = query(collections).collection("tableEntities").selectByIds(judged);
	const groups = findTableGroupsAt(state, tables.map((table) => getTableCenter(state, table)), { boxes });
	return tables.map((table, index) => {
		const groupId = groups[index]?.id ?? "";
		return {
			table,
			groupId,
			box: boxes.get(groupId) ?? null
		};
	});
}
var dropTablesIntoGroupsAction$ = (held) => function* (state) {
	yield getTableGroupDrops(state, held).filter(({ table, groupId }) => groupId !== getTableGroupId(state, table)).map(({ table, groupId }) => attachActionTag(Tag.drag, changeTableGroupAction({
		id: table.id,
		value: groupId,
		prevValue: table.groupId
	})));
};
var selectTableGroupAction$ = (id, $mod) => function* (state) {
	if (!$mod) yield unselectAllAction$();
	yield selectAction({ [id]: SelectType.tableGroup });
	yield changeTableGroupZIndexAction({
		id,
		zIndex: nextTableGroupZIndex(selectGroups(state))
	});
	yield raiseMembers(state, id);
};
function raiseMembers(state, groupId) {
	const { doc: { tableIds, memoIds }, collections } = state;
	const isMember = arrayHas(getTableGroupMemberIds(state, groupId));
	const tables = query(collections).collection("tableEntities").selectByIds(tableIds);
	const members = tables.filter((table) => isMember(table.id)).sort((a, b) => a.ui.zIndex - b.ui.zIndex);
	const others = [...tables.filter((table) => !isMember(table.id)), ...query(collections).collection("memoEntities").selectByIds(memoIds)];
	const base = Math.max(1, ...others.map(({ ui }) => ui.zIndex)) + 1;
	if (members.every(({ ui }) => ui.zIndex >= base)) return [];
	return members.map((table, index) => changeZIndexAction({
		id: table.id,
		zIndex: base + index
	}));
}
var selectTableGroupTablesAction$ = (id) => function* (state) {
	yield unselectAllAction$();
	yield selectAction(Object.fromEntries(getTableGroupMemberIds(state, id).map((tableId) => [tableId, SelectType.table])));
};
var actions$ = {
	addTableGroupAction$,
	addTableGroupFromTablesAction$,
	removeTableGroupAction$,
	moveTableGroupAction$,
	setTableGroupAction$,
	dropTablesIntoGroupsAction$,
	selectTableGroupAction$,
	selectTableGroupTablesAction$
};
//#endregion
export { actions$, addTableGroupAction$, addTableGroupFromTablesAction$, dropTablesIntoGroupsAction$, getCarriedTableIds, getTableGroupDrops, moveTableGroupAction$, removeTableGroupAction$, selectTableGroupAction$, selectTableGroupTablesAction$, setTableGroupAction$, toMoveTableGroupActions };
